import { useMemo, useState } from 'react'
import CLUB from '../../config/club.js'
import { fmtVsPar } from '../../lib/scoring.js'
import { SAMPLE_TOURNAMENT } from './sampleTournament.js'
import { buildReports, resultsCsv, rosterCsv } from './ggReports.js'

// G-Reports & Exports — isolated sandbox feature. READ-ONLY and self-contained:
// every report is computed from the in-code SAMPLE_TOURNAMENT (no DB). Printing
// reuses the app's window.print() pattern with a scoped print <style>; exports
// are a client-side CSV Blob download.
const VIEWS = [
  { key: 'leaderboard', label: 'Leaderboard' },
  { key: 'grossnet',    label: 'Gross + Net' },
  { key: 'winners',     label: 'Flight Winners' },
  { key: 'matches',     label: 'Match Results' },
  { key: 'calcutta',    label: 'Calcutta' },
]

function download(filename, text) {
  const blob = new Blob([text], { type: 'text/csv' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  a.click()
}

export default function GReports() {
  const [view, setView] = useState('leaderboard')
  const reports = useMemo(() => buildReports(SAMPLE_TOURNAMENT), [])

  return (
    <div className="card">
      <style>{PRINT_CSS}</style>
      <div className="g-screen flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex flex-wrap items-center gap-1 rounded-lg p-1" style={{ backgroundColor: '#F3F4F6', border: '1px solid #E5E7EB' }}>
          {VIEWS.map((v) => {
            const on = view === v.key
            return (
              <button key={v.key} onClick={() => setView(v.key)}
                className="rounded-md px-3 py-1 text-xs font-bold uppercase tracking-wider transition"
                style={on ? { backgroundColor: 'var(--ge-primary)', color: '#fff' } : { backgroundColor: 'transparent', color: '#374151' }}>
                {v.label}
              </button>
            )
          })}
        </div>
        <div className="flex gap-2">
          <button className="btn-ghost text-xs" onClick={() => download('results.csv', resultsCsv(reports))}>⬇ Results CSV</button>
          <button className="btn-ghost text-xs" onClick={() => download('roster.csv', rosterCsv(SAMPLE_TOURNAMENT))}>⬇ Roster CSV</button>
          <button className="btn-primary" onClick={() => window.print()}>🖨 Print</button>
        </div>
      </div>

      <div className="g-print">
        <div className="g-rpt-head">
          <img src={CLUB.logo} alt={CLUB.name} className="g-rpt-logo" />
          <div>
            <div className="g-rpt-club">{CLUB.name}</div>
            <div className="g-rpt-event">{reports.meta.tournamentName} · {reports.meta.date} · {VIEWS.find((v) => v.key === view)?.label}</div>
          </div>
        </div>

        {view === 'leaderboard' ? <Standings title="Overall Leaderboard (Gross)" rows={reports.overall.gross} /> : null}

        {view === 'grossnet' ? reports.flights.map((f) => (
          <div key={f.flightId} className="g-flight">
            <h4 className="g-flight-name">{f.flightName}</h4>
            <div className="g-two-col">
              <Standings title="Gross" rows={f.gross} />
              <Standings title="Net" rows={f.net} net />
            </div>
          </div>
        )) : null}

        {view === 'winners' ? reports.flights.map((f) => (
          <div key={f.flightId} className="g-flight">
            <h4 className="g-flight-name">{f.flightName}</h4>
            <div className="g-two-col">
              <Podium title="Gross" rows={f.winners.gross} />
              <Podium title="Net" rows={f.winners.net} net />
            </div>
          </div>
        )) : null}

        {view === 'matches' ? reports.flights.map((f) => (
          <div key={f.flightId} className="g-flight">
            <h4 className="g-flight-name">{f.flightName}</h4>
            {f.matches.length === 0 ? <p className="g-muted">No pairings.</p> : (
              <table className="g-tbl">
                <thead><tr><th>Match</th><th>Result</th><th>Winner</th></tr></thead>
                <tbody>
                  {f.matches.map((m, i) => (
                    <tr key={i}>
                      <td>{m.teamAName} vs {m.teamBName}</td>
                      <td>{m.result}</td>
                      <td>{m.winnerName}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )) : null}

        {view === 'calcutta' ? (
          <div className="g-flight">
            <h4 className="g-flight-name">Calcutta Payouts — pot ${reports.calcutta.pot.toLocaleString()}</h4>
            <table className="g-tbl">
              <thead><tr><th>Place</th><th>%</th><th>Amount</th><th>Winning Team</th><th>Owner</th></tr></thead>
              <tbody>
                {reports.calcutta.rows.map((r) => (
                  <tr key={r.place}>
                    <td>{r.place}</td><td>{r.pct}%</td><td>${r.amount.toLocaleString()}</td><td>{r.teamName}</td><td>{r.owner}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  )
}

function Standings({ title, rows, net }) {
  return (
    <div>
      <h5 className="g-sub">{title}</h5>
      <table className="g-tbl">
        <thead><tr><th>Pos</th><th>Team</th><th>Players</th><th>{net ? 'Net' : 'Gross'}</th><th>vs Par</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.teamId}>
              <td>{r.place}</td>
              <td>{r.teamName}</td>
              <td className="g-players">{(r.players || []).join(' / ')}</td>
              <td>{net ? '' : r.gross}</td>
              <td>{fmtVsPar(net ? r.vsPar : r.vsPar)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Podium({ title, rows, net }) {
  const medal = ['🥇', '🥈', '🥉']
  return (
    <div>
      <h5 className="g-sub">{title}</h5>
      <ul className="g-podium">
        {rows.map((r, i) => (
          <li key={r.teamId}><span>{medal[i] || `${i + 1}.`}</span> {r.teamName} <span className="g-muted">({fmtVsPar(r.vsPar)})</span></li>
        ))}
      </ul>
    </div>
  )
}

const PRINT_CSS = `
  .g-rpt-head { display: flex; align-items: center; gap: 14px; padding-bottom: 10px; border-bottom: 3px solid ${CLUB.accentColor}; margin-bottom: 14px; }
  .g-rpt-logo { height: 52px; object-fit: contain; }
  .g-rpt-club { font-weight: 800; font-size: 18px; color: ${CLUB.primaryColor}; }
  .g-rpt-event { font-size: 12px; color: #555; }
  .g-flight { margin-bottom: 18px; }
  .g-flight-name { font-weight: 800; color: ${CLUB.primaryColor}; margin: 10px 0 6px; }
  .g-two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
  .g-sub { font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em; color: #888; margin: 0 0 4px; }
  .g-tbl { width: 100%; border-collapse: collapse; font-size: 12px; }
  .g-tbl th, .g-tbl td { border: 1px solid #ddd; padding: 4px 6px; text-align: left; }
  .g-tbl th { background: #f6f6f2; color: #555; }
  .g-players { color: #666; }
  .g-podium { list-style: none; padding: 0; margin: 0; font-size: 14px; }
  .g-podium li { padding: 3px 0; }
  .g-muted { color: #999; font-size: 12px; }
  @media print {
    body * { visibility: hidden !important; }
    .g-print, .g-print * { visibility: visible !important; }
    .g-print { position: absolute; left: 0; top: 0; width: 100%; }
    .g-screen { display: none !important; }
    @page { size: portrait; margin: 0.4in; }
  }
`
